import { Injectable } from '@angular/core';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Observable } from 'rxjs/internal/Observable';

import { SharedUtilsService } from './shared-utils.service';
import { SsModuleService } from './ss-module.service';
import { GenericModuleService } from './generic-module.service';

@Injectable({ providedIn: 'root' })
export class TeamService {
  public globalTeam = null;

  // In-memory team cache (replaces Dexie IndexedDB storage).
  private cachedTeams: any[] = [];
  private cacheExpiresAt: number = 0;
  private cacheLifetime = 1000 * 60 * 60 * 4; // 4 hours

  constructor(
    private _genericModuleService: GenericModuleService,
    private _sharedUtilsService: SharedUtilsService,
    private _ssModuleService: SsModuleService
  ) { }

  invalidateTeamCache(): void {
    this.cachedTeams = [];
    this.cacheExpiresAt = 0;
  }


  async getAllTeams(_getFreshFromApi: boolean = false): Promise<any[]> {
    try {
      // Use in-memory cache if available and not forcing fresh.
      if (!_getFreshFromApi && this.cachedTeams.length && Date.now() < this.cacheExpiresAt) {
        return this.cachedTeams;
      }

      return await this.fetchTeamsFromApi();
    } catch (_err) {
      console.log('There was an issue getting teams from API.', _err?.message);
      return [];
    }
  }


  private async fetchTeamsFromApi(): Promise<any[]> {
    try {
      const teamModule = await this._ssModuleService.waitForModule('teams');

      const genericSearchData = {
        name: 'teams',
        schema: teamModule.customSchema,
        searchTerms: { deleted: false, active: true },
        relationshipsNeeded: null
      };

      const apiTeams = await lastValueFrom(this._genericModuleService.search(genericSearchData)) ?? [];
      this.cachedTeams = apiTeams;
      this.cacheExpiresAt = Date.now() + this.cacheLifetime;
      return apiTeams;
    } catch (_err) {
      console.log('Failed to fetch teams from API:', _err?.message);
      return [];
    }
  }


  getTeam(id: string, _getFreshFromApi: boolean = false): Promise<any> {
    return new Promise(async (resolve) => {
      try {
        let foundTeam: any = null;

        if (id) {
          // Try in-memory cache first.
          if (!_getFreshFromApi && this.cachedTeams.length && Date.now() < this.cacheExpiresAt) {
            foundTeam = this.cachedTeams.find(_t => _t._id === id) ?? null;
          }

          // Fetch from API if not found in cache.
          if (!foundTeam) {
            foundTeam = await this.fetchTeamFromApi(id);
          }
        }

        resolve(foundTeam);
      } catch (_err) {
        console.log(`There was an issue getting team with ID ${id}:`, _err?.message);
        resolve(null);
      }
    });
  }


  private async fetchTeamFromApi(id: string): Promise<any> {
    let foundTeam: any = null;

    if (id) {
      try {
        const teamModule = await this._ssModuleService.waitForModule('teams');

        const genericSearchData = {
          name: 'teams',
          schema: teamModule.customSchema,
          searchTerms: { moduleType: 'teams', _id: id },
          relationshipsNeeded: teamModule?.relationships ?? []
        };

        foundTeam = await lastValueFrom(this._genericModuleService.getWithRelationships(genericSearchData));
      } catch (err) {
        console.log(`Failed to fetch team from API with ID ${id}:`, err);
      }
    }

    return foundTeam;
  }


  updateTeam(_team: any): Promise<any> {
    return new Promise(async (resolve) => {
      try {
        let updatedTeam: any = null;

        // Make sure a team was passed.
        if (_team?._id) {
          const teamModule = await this._ssModuleService.waitForModule('teams');

          // Build update object.
          const customModuleUpdate = {
            record: _team,
            name: 'teams',
            schema: teamModule.customSchema
          };

          updatedTeam = await lastValueFrom(this._genericModuleService.update(customModuleUpdate)) ?? null;
          if (updatedTeam?._id) this.invalidateTeamCache();
        }

        resolve(updatedTeam);
      } catch (_err) {
        console.log(`There was an issue updating team with ID ${_team?._id}:`, _err?.message || _err);
        resolve(null);
      }
    });
  }


  getGlobalTeam() {
    return new Promise(async (resolve) => {
      if (!this.globalTeam) {
        const teams = await this.getAllTeams();
        this.globalTeam = teams?.find(_t => _t?.name === 'Global');
      }

      // console.log('Global Team: ', this.globalTeam);

      resolve(this.globalTeam);
    });
  }


  getTeamsForSearch(_currentUser): Promise<any[]> {
    return new Promise(async (resolve) => {
      let teamsToSearch = [];

      // console.log('Getting teams for user: ', _currentUser);

      // User had no teams set or was empty
      if (!_currentUser.teams || _currentUser.teams === undefined || !_currentUser.teams.length) {
        const globalTeam = await this.getGlobalTeam();
        teamsToSearch = (globalTeam && globalTeam !== undefined) ? [globalTeam['_id']] : [];
      } else {
        // teamsToSearch = _currentUser.teams.map(_t => _t._id ? _t._id : _t);
        teamsToSearch = _currentUser.teams; // .map(_t => _t._id ? _t._id : _t);
      }

      resolve(teamsToSearch);
    });
  }


  // Returns true when the passed user is a member of a team with the given name. Case-sensitive match; user.teams is expected to be populated (autoPopulate runs on User finds so getCurrentlyLoggedInUser() satisfies this).
  isUserInTeam(_user: any, _teamName: string): boolean {
    if (!_user || !_teamName) return false;
    return (_user?.teams || []).some((_t: any) => _t?.name === _teamName);
  }


  async findTeamByNameVariants(_nameVariants: string[], _returnAllMatching: boolean = false): Promise<any> {
    let foundTeams: any[] = [];

    if (_nameVariants?.length) {
      try {
        const teamModule = await this._ssModuleService.waitForModule('teams');
        const nonRepeatedVariants: string[] = []; // Holds non-repeated names.

        // Make sure each name only exists once in array passed to search.
        for (const _v of _nameVariants) {
          const variant: string = _v?.trim();
          if (variant?.length && !nonRepeatedVariants.includes(variant)) nonRepeatedVariants.push(variant);
        }

        // Build the search object.
        const searchData = {
          name: 'teams',
          schema: teamModule.customSchema,
          searchTerms: { team_name_variants: nonRepeatedVariants },
          relationshipsNeeded: null
        };

        // Do search for matching teams.
        foundTeams = await lastValueFrom(this._genericModuleService.search(searchData)) ?? [];
        // console.log('foundTeams: ', foundTeams);
      } catch (_err) {
        console.log(`Failed to fetch team by name variants:`, _err?.message || _err);
      }
    }

    // If user wants all return the array. Otherwise, just return the first or null.
    return _returnAllMatching ? foundTeams : foundTeams[0] ?? null;
  }
}